/* eslint-disable max-lines */
import { action, autorun, computed, makeObservable, observable, toJS } from 'mobx';

import { OrgUserStatus } from '@api';
import { buildCognitoLogoutUrl } from '@auth/common/cognitoLogout';
import { Region } from '@components/common/constants';
import { isRudderUser } from '@components/common/util/util';
import { Analytics, webapp } from '@lib/analytics';
import type { webappTypes } from '@lib/analytics';
import { bugsnagSetUser, webappClient } from '@lib/bugsnag';
import { experiment } from '@lib/experiment';
import { loadSchedulerWidget } from '@lib/scheduler';
import { apiAuthCaller } from '@services/apiCaller';
import auth, { CognitoTokens, MfaMethod, OrgOption } from '@services/cognito';
import { amplitudeEngagement } from '@services/amplitudeEngagement';
import { customerIoInApp } from '@services/customerIoInApp';
import { IRootStore } from '@stores/index';
import { WorkspaceEnvironment } from '@stores/workspace';
import { ACCESSIBLE_WORKSPACE_STATUSES, WorkspaceStatus } from '@stores/workspace/constants';
import LocalStorage from '@utils/localStorage';
import { CatchErr } from '@utils/types';

import { OrganizationRole, UserRoles, WorkspaceRole } from '../member';
import type { Traits, TraitsV2 } from './types';

export enum LoginMfaState {
  None = '',
  ForcedSetup = 'mfa_setup',
  Challenge = 'mfa_challenge',
  GracePeriod = 'mfa_grace_period',
}
export enum PendingTask {
  databricks = 'DATABRICKS_ONBOARDING',
}
export type Provider = 'Google' | '';
type MFAStatus = 'SMS' | 'TOTP' | 'NOMFA';
interface IMfaData {
  sessionToken?: string;
  status?: string;
  email?: string;
  provider?: string;
  providerUserId?: string;
  state?: LoginMfaState;
  mfaMethod?: MfaMethod;
  availableMethods?: MfaMethod[];
  gracePeriodDeadline?: string;
}
interface IUserData {
  email: string;
  id: string;
  name: string;
  provider: Provider;
  providerUserId: string;
  workspaces: IWorkspace[];
  phoneNumber: string;
  mfaStatus: MFAStatus;
  enforcedMfa: boolean;
  hasTotpEnabled: boolean;
  canSetupMfa: boolean;
  organizations: IOrganization[];
  pendingTasks: PendingTask[];
  createdAt: string;
  updatedAt: string;
}
interface IOrganization {
  id: string;
  name: string;
  role: OrganizationRole;
  planType: string;
  status?: OrgUserStatus;
}

export type { Traits, TraitsV2 } from './types';
interface IWorkspace {
  id: string;
  name: string;
  organizationId: string;
  environment: WorkspaceEnvironment;
  roles: WorkspaceRole[];
  defaultRegion: Region;
  status: WorkspaceStatus;
  v2WorkspaceAccess: boolean;
}

export interface IUserStore {
  setUserSessionForMFA(email: string, mfaData: IMfaData): void;
  setAndRefreshToken(token: string): Promise<void>;
  id: string;
  name: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  token: string;
  loggedIn: boolean;
  invitedUserFirstTimeLogin: boolean;
  firstTimeLogin: boolean;
  sessionData: CognitoTokens;
  workspaces: Array<IWorkspace>;
  selectedWorkspaceId: string;
  selectedOrganizationId: string;
  isOrgAdmin: boolean;
  roles: UserRoles;
  rootStore: IRootStore;
  provider: Provider;
  providerUserId: string;
  mfaData: IMfaData | undefined;
  phoneNumber: string;
  mfaStatus: MFAStatus;
  enforcedMfa: boolean;
  hasTotpEnabled: boolean;
  canSetupMfa: boolean;
  signUp(name: string, organization: string, email?: string): void;
  setName(name: string): void;
  /* called after the actual login to fetch user details */
  loginUser(sessionData: CognitoTokens): Promise<void>;
  trackOnboarding(traits: Traits): void;
  trackOnboardingV2(traits: TraitsV2): void;
  identifyFoundation(extra?: Partial<webappTypes.IdentifyTraits>): void;
  setLoginMetadata(metadata: { invited?: boolean; firstTimeLogin?: boolean }): void;
  setProvider(provider: Provider, providerUserId: string): void;
  signOutUser(signOutfromBackend?: boolean, globalSignout?: boolean): Promise<void>;
  signOutUserAndClearHostedUiSession(): Promise<void>;
  isFederatedLogin: boolean;
  switchWorkspace(workspaceId: string): Promise<void>;
  updateEnforcedMfa(): Promise<void>;
  setPhoneNumber(number: string): void;
  setMfaStatus(value: MFAStatus): void;
  getUser(): Promise<void>;
  isSso: boolean;
  isGoogleOrSso: boolean;
  organizations: IOrganization[];
  accessibleOrganizations: IOrganization[];
  pendingTasks: PendingTask[];
  showDatabricksWorkspace: boolean;
  setShowDatabricksWorkspace(val: boolean): void;
  removePendingTask(task: PendingTask): void;
  selectedWorkspaceRegion: Region;
  selectedWorkspace: IWorkspace | undefined;
  selectedOrganization: IOrganization | undefined;
  isUserSuspendedInSelectedOrganization: boolean;
  accessibleWorkspaces: IWorkspace[];
  ensureProductionWorkspaceSelected(): boolean;
  showGetStarted: boolean;
  setShowGetStarted(val: boolean): void;
  ssoOrgOptions: OrgOption[];
  setSsoOrgOptions(options: OrgOption[]): void;
  clearSsoOrgOptions(): void;
}

type LocalUserStore = Pick<
  UserStore,
  'token' | 'loggedIn' | 'email' | 'provider' | 'selectedWorkspaceId'
> & {
  refreshToken: UserStore['sessionData']['refreshToken'];
};

function autoSave(store: UserStore) {
  let firstRun = true;

  autorun(() => {
    const userStore = toJS(store);

    if (!firstRun) {
      const json: LocalUserStore = {
        refreshToken: userStore.sessionData.refreshToken,
        loggedIn: userStore.loggedIn,
        token: userStore.token,
        email: userStore.email,
        provider: userStore.provider,
        // workspaceId is required in destination oauth callback route to make API call
        selectedWorkspaceId: userStore.selectedWorkspaceId,
      };
      LocalStorage.set('userStore', JSON.stringify(json));
    }
    firstRun = false;
  });
}

export class UserStore implements IUserStore {
  @observable public id = '';

  @observable public name = '';

  @observable public email = '';

  @observable public createdAt = '';

  @observable public updatedAt = '';

  @observable public token = '';

  @observable public sessionData: CognitoTokens = {
    idToken: '',
    refreshToken: '',
  };

  @observable public loggedIn = false;

  @observable public invitedUserFirstTimeLogin = false;

  @observable public firstTimeLogin = false;

  @observable enforcedMfa = false;

  @observable public workspaces: IWorkspace[] = [];

  @observable public organizations: IOrganization[] = [];

  @observable public selectedWorkspaceId = '';

  @observable public rootStore: IRootStore;

  @observable public provider: Provider = '';

  @observable public providerUserId = '';

  @observable public mfaData: IMfaData | undefined;

  @observable phoneNumber = '';

  @observable mfaStatus: MFAStatus = 'NOMFA';

  @observable canSetupMfa = false;

  @observable pendingTasks: PendingTask[] = [];

  @observable showDatabricksWorkspace = true;

  @observable showGetStarted = false;

  @observable public ssoOrgOptions: OrgOption[] = [];

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.load();
    autoSave(this);
    this.rootStore = rootStore;
  }

  public load() {
    const userStore = LocalStorage.get('userStore');
    if (userStore) {
      const store: LocalUserStore = JSON.parse(userStore);
      this.sessionData = { refreshToken: store.refreshToken, idToken: store.token };
      this.token = store.token;
      this.loggedIn = store.loggedIn;
      this.email = store.email;
      this.selectedWorkspaceId = store.selectedWorkspaceId;
      this.provider = store.provider;
    }
  }

  @action.bound
  public setShowGetStarted(val: boolean) {
    this.showGetStarted = val;
  }

  @action.bound
  public setSsoOrgOptions(options: OrgOption[]) {
    this.ssoOrgOptions = options;
  }

  @action.bound
  public clearSsoOrgOptions() {
    this.ssoOrgOptions = [];
  }

  @action.bound
  public setName(name: string): void {
    this.name = name;
  }

  @action.bound
  public setProvider(provider: Provider, providerUserId: string) {
    this.provider = provider;
    this.providerUserId = providerUserId;
  }

  @action.bound
  public setPhoneNumber(number: string) {
    this.phoneNumber = number;
  }

  @action.bound
  public setShowDatabricksWorkspace(val: boolean) {
    this.showDatabricksWorkspace = val;
  }

  @action.bound
  public setMfaStatus(value: MFAStatus) {
    this.mfaStatus = value;
  }

  @action.bound
  public signUp(name: string, organization: string, email?: string) {
    this.loggedIn = false;
    this.name = name;
    this.rootStore.organizationStore.name = organization;
    if (email) {
      this.email = email;
    }
  }

  @action.bound async setAndRefreshToken(idToken: string) {
    if (idToken !== this.token) {
      this.token = idToken;
    }
    let shouldSignOut = false;
    // Gets current session. Tokens are refreshed by amplify automatically when they expire
    try {
      const res = await auth.currentSession(this.sessionData, this.email);

      if (res.status === 'success' && res.idToken) {
        this.token = res.idToken;
        this.sessionData.idToken = res.idToken;
        this.sessionData.refreshToken = res.refreshToken;
      }
    } catch (error: CatchErr) {
      if (error && error.response && error.response.status === 400) {
        shouldSignOut = true;
      }
    }

    // doing the same thing as error response interceptor
    if (shouldSignOut) {
      await this.signOutUser();
    }
  }

  @action.bound
  public setUserSessionForMFA(email: string, mfaData: IMfaData) {
    this.email = email;
    this.mfaData = mfaData;
    this.sessionData.idToken = mfaData.sessionToken as string;
  }

  @action.bound
  async updateEnforcedMfa() {
    const userData: { data: IUserData } = await apiAuthCaller({
      nonWorkspaceRoute: true,
      region: Region.US,
    }).get('/getUser');
    const { enforcedMfa } = userData.data;
    this.enforcedMfa = enforcedMfa;
  }

  @action.bound
  async loginUser(sessionData: CognitoTokens) {
    this.setSessionToken(sessionData);
    await this.getUser();
  }

  public async getUser() {
    try {
      const userData = await apiAuthCaller({
        nonWorkspaceRoute: true,
        region: Region.US,
      }).get<IUserData>('/getUser');

      const {
        id,
        name,
        email,
        phoneNumber,
        mfaStatus,
        enforcedMfa,
        workspaces,
        canSetupMfa,
        provider,
        providerUserId,
        organizations,
        pendingTasks = [],
        createdAt,
        updatedAt,
      } = userData.data;

      this.id = id;
      this.email = email;
      this.name = name;
      this.createdAt = createdAt;
      this.updatedAt = updatedAt;
      this.phoneNumber = phoneNumber;
      this.mfaStatus = mfaStatus;
      this.enforcedMfa = enforcedMfa;
      this.canSetupMfa = canSetupMfa;
      this.pendingTasks = pendingTasks;
      this.organizations = organizations;
      this.workspaces = workspaces;
      this.selectedWorkspaceId = this.getInitialWorkspaceId();
      this.saveCurrentWorkspace();
      // Preserve grace period / forced setup state across getUser() — addMfa page reads it
      if (
        this.mfaData?.state !== LoginMfaState.GracePeriod &&
        this.mfaData?.state !== LoginMfaState.ForcedSetup
      ) {
        this.mfaData = {};
      }
      this.loggedIn = true;
      this.setProvider(provider, providerUserId);

      const {
        selectedOrganizationId,
        selectedWorkspaceId,
        selectedWorkspace,
        selectedOrganization,
      } = this;

      await experiment.identify({
        id,
        workspaceId: selectedWorkspaceId,
        organizationId: selectedOrganizationId,
        planType: selectedOrganization?.planType,
        environment: selectedWorkspace?.environment,
      });

      this.identifyFoundation();

      bugsnagSetUser(this);

      // Feature flags are ready now that identify has resolved — load the sales
      // scheduling widget (Calendly or Qualified) based on the feature flag.
      // Reported rather than swallowed by the surrounding catch so a failure here
      // is visible without blocking the rest of the login flow.
      try {
        loadSchedulerWidget({ email: this.email, name: this.name });
      } catch (err) {
        webappClient.notify(err as Error);
      }

      // Also flag-gated, so it likewise has to wait for identify. Deliberately
      // not awaited: the SDK is fetched lazily over the network and the login
      // flow must not block on it (boot() reports its own failures).
      // Identified by email, not `id` — see the note on boot().
      customerIoInApp.boot(this.email);
    } catch (err) {}
  }

  public identifyFoundation(extra?: Partial<webappTypes.IdentifyTraits>) {
    const {
      id,
      email,
      name,
      createdAt,
      roles,
      rootStore,
      selectedOrganizationId,
      selectedWorkspaceId,
      selectedWorkspace,
      selectedWorkspaceRegion,
    } = this;
    const { organizationStore, billingStore, trialStore } = rootStore;
    webapp.identify(id, {
      email,
      environment: selectedWorkspace?.environment ?? 'PRODUCTION',
      isTrial: trialStore.hasActiveTrials,
      organizationId: selectedOrganizationId || '',
      planName: organizationStore.planName,
      region: selectedWorkspaceRegion,
      role: roles?.organization,
      userCreatedAt: createdAt,
      userName: name,
      workspaceId: selectedWorkspaceId,
      billingCycle: billingStore.currentBillingCycle,
      isInternal: email ? isRudderUser(email) : false,
      isGracePeriod: trialStore.isPlanTrialInGracePeriod,
      ...(trialStore.planTrial?.createdAt && { trialStartDate: trialStore.planTrial.createdAt }),
      ...(trialStore.planTrial?.endsAt && { trialEndDate: trialStore.planTrial.endsAt }),
      ...(trialStore.planTrial?.graceEndsAt && {
        gracePeriodEndDate: trialStore.planTrial.graceEndsAt,
      }),
      ...extra,
    });
  }

  private setSessionToken(session: CognitoTokens) {
    this.sessionData = session;
    this.token = this.sessionData.idToken;
  }

  trackOnboarding(traits: Traits) {
    this.identifyFoundation(traits);
    Analytics.track({ event: 'userCompletedOnboarding', properties: traits });
  }

  trackOnboardingV2(traits: TraitsV2) {
    const {
      onboardingQuestionsStatus,
      questionMigratingFrom,
      questionProductInterest,
      personaType,
      questionHowDidYouHear,
    } = traits;
    this.identifyFoundation({
      onboardingQuestionsStatus,
      questionMigratingFrom,
      questionProductInterest,
      questionHowDidYouHear,
      userCompanyRole: personaType ? [personaType] : undefined,
    });
    Analytics.track({
      event: 'userCompletedOnboarding',
      properties: (onboardingQuestionsStatus === 'skipped'
        ? { onboardingQuestionsStatus }
        : {
            onboardingQuestionsStatus,
            personaType,
            questionHowDidYouHear,
            questionProductInterest,
            questionMigratingFrom,
            userCompanyRole: personaType ? [personaType] : undefined,
          }) as Traits,
    });
  }

  setLoginMetadata({
    invited = false,
    firstTimeLogin = false,
  }: {
    invited?: boolean;
    firstTimeLogin?: boolean;
  }) {
    this.invitedUserFirstTimeLogin = firstTimeLogin && !!invited;
    this.firstTimeLogin = firstTimeLogin;

    if (this.firstTimeLogin) {
      Analytics.track({ event: 'ads_cnv_app_signup', properties: {} });
    }
  }

  public get accessibleWorkspaces() {
    return this.workspaces.filter((workspace) =>
      ACCESSIBLE_WORKSPACE_STATUSES.includes(workspace.status),
    );
  }

  @computed get accessibleOrganizations() {
    return this.organizations.filter(
      (organization) => organization.status !== OrgUserStatus.SUSPENDED,
    );
  }

  private getInitialWorkspaceId() {
    if (this.accessibleWorkspaces.length === 0) {
      return '';
    }

    const lastVisitedWorkspaceId = LocalStorage.get('lastVisitedWorkspace') || '';
    const lastVisitedWorkspace = this.accessibleWorkspaces.find(
      (workspace) => workspace.id === lastVisitedWorkspaceId,
    );

    if (lastVisitedWorkspace) {
      return lastVisitedWorkspace.id;
    }

    // No remembered workspace — default to the production one, falling back to
    // the last accessible workspace (e.g. a development-only organization).
    const productionWorkspace = this.accessibleWorkspaces.find(
      (workspace) => workspace.environment === 'PRODUCTION',
    );

    return (
      productionWorkspace?.id ?? this.accessibleWorkspaces[this.accessibleWorkspaces.length - 1].id
    );
  }

  removePendingTask(task: PendingTask) {
    const tasks = this.pendingTasks;
    this.pendingTasks = tasks.filter((t) => t !== task);
  }

  private async signOutFromBackend(globalSignout = false): Promise<void> {
    try {
      await apiAuthCaller({
        idToken: this.sessionData.idToken,
        nonWorkspaceRoute: true,
        region: Region.US,
      })
        .ignore400()
        .post(
          '/signout',
          { refreshToken: this.sessionData.refreshToken },
          { params: { globalSignout } },
        );
    } catch (err) {}

    if (globalSignout) {
      await this.setAndRefreshToken(this.sessionData.idToken);
    }
  }

  @action.bound
  public async signOutUser(signOutfromBackend = false, globalSignout = false) {
    if (signOutfromBackend) {
      await this.signOutFromBackend(globalSignout);
    }

    this.loggedIn = false;
    this.id = '';
    this.name = '';
    this.email = '';
    this.token = '';
    this.sessionData = {
      refreshToken: '',
      idToken: '',
    };
    this.provider = '';
    this.providerUserId = '';
    this.workspaces = [];
    this.selectedWorkspaceId = '';
    this.mfaData = undefined;
    this.phoneNumber = '';
    this.mfaStatus = 'NOMFA';
    this.enforcedMfa = false;
    this.canSetupMfa = false;

    this.resetStores();

    amplitudeEngagement.shutdown();

    customerIoInApp.shutdown();

    experiment.reset();

    bugsnagSetUser(this);
    try {
      await import('@aws-amplify/auth').then((res) => res.Auth.signOut());
    } catch (err) {}
  }

  /**
   * User-initiated logout ("Log out" button) for federated (Hosted-UI) sessions.
   *
   * After the normal sign-out, redirects the browser through the Cognito
   * Hosted-UI logout endpoint to clear its session cookie — otherwise a federated
   * user can re-login within ~1h reusing the Cognito session, bypassing IdP-side
   * access removal. The logout URL is captured before `signOutUser` resets
   * `providerUserId`, and the redirect runs last since it navigates away.
   */
  @action.bound
  public async signOutUserAndClearHostedUiSession() {
    const logoutUrl = this.isFederatedLogin ? buildCognitoLogoutUrl() : '';

    await this.signOutUser(true);

    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  }

  private saveCurrentWorkspace() {
    LocalStorage.set('lastVisitedWorkspace', this.selectedWorkspaceId);
  }

  @action.bound
  public async switchWorkspace(workspaceId: string) {
    const { userStore } = this.rootStore;

    this.resetStores();
    this.selectedWorkspaceId = workspaceId;
    this.saveCurrentWorkspace();

    await experiment.identify({
      id: userStore.id,
      workspaceId: this.selectedWorkspaceId,
      organizationId: this.selectedOrganizationId,
      planType: this.selectedOrganization?.planType,
      environment: this.selectedWorkspace?.environment,
    });
  }

  /**
   * Ensures the production workspace of the selected organization is the active
   * one — used while the trial blocks the app so billing/upgrade actions target
   * the right workspace. Returns whether a switch was performed, so callers can
   * defer to the reload the switch triggers.
   */
  @action.bound
  public ensureProductionWorkspaceSelected(): boolean {
    const productionWorkspace = this.accessibleWorkspaces.find(
      (workspace) =>
        workspace.organizationId === this.selectedOrganizationId &&
        workspace.environment === 'PRODUCTION',
    );

    if (!productionWorkspace || productionWorkspace.id === this.selectedWorkspaceId) {
      return false;
    }

    this.switchWorkspace(productionWorkspace.id);
    return true;
  }

  @computed get selectedOrganizationId() {
    const workspace = this.workspaces.find(
      (workspace) => workspace.id === this.selectedWorkspaceId,
    );
    return workspace?.organizationId || '';
  }

  @computed get isOrgAdmin() {
    return this.roles?.organization === OrganizationRole.admin;
  }

  @computed get roles() {
    const member = this.rootStore.organizationStore.members.find(
      (member) => member.email === this.email,
    );
    return member?.roles!;
  }

  @computed get isSso() {
    return !!this.providerUserId && this.provider !== 'Google';
  }

  /**
   * True when the session was established through the Cognito Hosted UI
   * (federated login — SAML SSO or Google social), which is the only case that
   * leaves a Hosted-UI session cookie to clear on logout.
   */
  @computed get isFederatedLogin() {
    return !!this.providerUserId;
  }

  @computed get hasTotpEnabled() {
    return this.mfaStatus === 'TOTP';
  }

  @computed get isGoogleOrSso() {
    if (this.isSso) {
      return true;
    }

    // Handle Google users logging in with password
    let decodedPayload = {} as Record<string, string>;
    try {
      const jwtPayload = this.token.split('.')[1] || '';
      decodedPayload = JSON.parse(atob(jwtPayload) || '{}');
    } catch (error) {
      return false;
    }

    return decodedPayload['cognito:username']?.startsWith('Google');
  }

  @computed get selectedWorkspaceRegion(): Region {
    const workspace = this.workspaces.find(
      (workspace) => workspace.id === this.selectedWorkspaceId,
    );
    return workspace?.defaultRegion || Region.US;
  }

  @computed get selectedWorkspace() {
    return this.workspaces.find((workspace) => workspace.id === this.selectedWorkspaceId);
  }

  @computed get selectedOrganization() {
    return this.organizations.find(
      (organization) => organization.id === this.selectedOrganizationId,
    );
  }

  @computed get isUserSuspendedInSelectedOrganization() {
    return this.selectedOrganization?.status === OrgUserStatus.SUSPENDED;
  }

  @action.bound
  private resetStores() {
    const {
      billingStore,
      sourcesListStore,
      destinationsListStore,
      transformationsListStore,
      librariesListStore,
      trackingPlanListStore,
      modelListStore,
      audienceListStore,
      profilesListStore,
      workspaceStore,
      organizationStore,
      featuresStore,
      settingsStore,
      retlSourceListStore,
      retlConnectionListStore,
      credentialStore,
      serviceAccessTokenStore,
    } = this.rootStore;

    workspaceStore.reset();
    organizationStore.reset();
    settingsStore.reset();
    billingStore.reset();
    featuresStore.reset();
    sourcesListStore.reset();
    destinationsListStore.reset();
    transformationsListStore.reset();
    librariesListStore.reset();
    trackingPlanListStore.reset();
    modelListStore.reset();
    audienceListStore.reset();
    profilesListStore.reset();
    retlSourceListStore.reset();
    retlConnectionListStore.reset();
    credentialStore.reset();
    serviceAccessTokenStore.reset();
  }
}
